<template>
  <b-modal
    id="ack-modal"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
    modal-class="ack-modal"
  >
    <div class="ack-modal-body">

      <!-- Appreciation banner -->
      <div class="ack-celebration">
        <div class="ack-celebration-icon">🎉</div>
        <p class="ack-celebration-title">Nice work.</p>
        <p class="ack-celebration-sub">You've completed a process.</p>
      </div>

      <!-- Divider -->
      <div class="ack-divider"></div>

      <!-- Peer prompt -->
      <div class="ack-peer-prompt">
        <i class="fas fa-hands ack-peer-icon"></i>
        <p class="ack-peer-label">
          <template>Would you like to acknowledge the person who held space?</template>
        </p>
      </div>

      <!-- Loading -->
      <div v-if="loadingPeers" class="ack-loading">
        <i class="fas fa-atom fa-spin"></i>
      </div>

      <!-- No peers -->
      <div v-if="!loadingPeers && peers.length === 0" class="ack-empty">
        No peers found for this contraction.
      </div>

      <!-- Peer list -->
      <div v-if="peers.length" class="peer-list">
        <div
          v-for="peer in peers"
          :key="peer.id"
          class="peer-item"
          :class="{ 'peer-item--selected': isSelected(peer.id) }"
          @click="togglePeer(peer.id)"
        >
          <img
            :src="peer.profile_image || 'https://media.iamconnected.com/profile_images/default.png'"
            class="peer-avatar"
            alt=""
          />
          <span class="peer-name">{{ peer.first_name }} {{ peer.last_name }}</span>
          <i v-if="isSelected(peer.id)" class="fas fa-check-circle peer-check"></i>
        </div>
      </div>

      <!-- Custom message -->
      <div v-if="!loadingPeers && peers.length" class="ack-message-wrap">
        <label style="font-size: 0.75rem" class="pl-1 mb-0">Message:</label>
        <textarea
          v-model="thanksMessage"
          class="ack-message-input"
          placeholder="Add a personal message (optional)..."
          :disabled="!selectedPeers.length"
          rows="3"
        ></textarea>
      </div>

      <!-- Actions -->
      <div class="ack-footer">
        <button class="ack-btn ack-btn--cancel mr-auto" @click="cancelModal">Cancel</button>
        <button
          class="ack-btn ack-btn--thanks"
          :class="{ 'ack-btn--thanks-active': selectedPeers.length && !sending }"
          :disabled="(selectedPeers.length === 0) || sending"
          @click="resolveProcessingRequest(true)"
        >
          🙏 {{ sending ? 'Sending...' : 'Send thanks' }}
        </button>
        <button class="ack-btn ack-btn--close" @click="resolveProcessingRequest()" :disabled="sending">
          Skip thanks
        </button>
      </div>

    </div>
  </b-modal>
</template>

<script>
import axios from "@/axios";

export default {
  name: "AckModal",
  data() {
    return {
      processing_request_id: null,
      contraction_id: null,
      peers: [],
      connections: [],
      loadingPeers: false,
      selectedPeers: [],
      sending: false,
      thanksMessage: '',
      modalContraction: null,
      defaultMessage: 'Thanks for holding space — I really appreciate your support.',
    };
  },
  methods: {
    async openForContraction(contractionId) {
      this.contraction_id = contractionId;
      this.processing_request_id = null;
      this.selectedPeers = [];
      this.peers = [];
      this.thanksMessage = this.defaultMessage;
      this.$bvModal.show('ack-modal');
      this.loadingPeers = true;
      this.peers = await this.getConnections();
      this.loadingPeers = false;
    },

    async openForRequest(req) {
      if (req.contraction_id) {
        await this.openForContraction(req.contraction_id);
        if (! this.peers.some(p => p.id == req.target_user_id)) {
          this.peers.push({ id: req.target_user_id, first_name: req.target_name });
        }
        return;
      }
      this.processing_request_id = req.processing_request_id;
      this.contraction_id = req.contraction_id;
      this.selectedPeers = [];
      this.peers = [{ id: req.target_user_id, first_name: req.target_name }];
      this.thanksMessage = this.defaultMessage;
      this.$bvModal.show('ack-modal');
    },

    async getConnections() {
      if (this.connections.length) {
        return this.connections;
      }
      return axios.get('connections/connected/list').then(r => {
        return this.connections = r.data || [];
      });
    },

    async resolveProcessingRequest(sendThanks) {
      this.sending = true;
      await axios.post('processingRequest/resolve', {
        users: sendThanks ? this.selectedPeers : [],
        message: this.thanksMessage,
        contraction_id: this.contraction_id,
        processing_request_id: this.processing_request_id
      });
      this.sending = false;
      this.$bvModal.hide('ack-modal');
      this.$emit('resolved');
    },

    cancelModal() {
      this.$bvModal.hide('ack-modal');
      this.$emit('cancelled');
    },

    isSelected(peerId) {
      return this.selectedPeers.some(id => id === peerId);
    },

    togglePeer(peerId) {
      const idx = this.selectedPeers.findIndex(id => id === peerId);
      if (idx === -1) {
        this.selectedPeers.push(peerId);
      } else {
        this.selectedPeers.splice(idx, 1);
      }
    },
  },
};
</script>

<style>
/* Acknowledge modal — global so it applies inside the BootstrapVue portal */
.ack-modal-body {
  padding: 8px 4px 4px;
}

.ack-celebration {
  text-align: center;
  padding: 16px 8px 12px;
}

.ack-celebration-icon {
  font-size: 2rem;
  margin-bottom: 8px;
}

.ack-celebration-title {
  font-weight: 800;
  font-size: 1.15rem;
  color: #222;
  margin: 0 0 2px;
}

.ack-celebration-sub {
  font-size: 0.88rem;
  color: #888;
  margin: 0;
}

.ack-divider {
  height: 1px;
  background: #f0ece3;
  margin: 24px 0;
}

.ack-peer-prompt {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}

.ack-peer-icon {
  color: #d6b674;
  font-size: 1rem;
}

.ack-peer-label {
  font-weight: 700;
  font-size: 0.9rem;
  color: #333;
  margin: 0;
}

.ack-message-wrap {
  margin-top: 14px;
}

.ack-message-input {
  width: 100%;
  border: 1.5px solid #f0ece3;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.85rem;
  color: #444;
  resize: none;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
}

.ack-message-input:focus {
  border-color: #d6b674;
}

.ack-loading,
.ack-empty {
  text-align: center;
  color: #bbb;
  font-size: 0.85rem;
  padding: 16px 0;
}

.peer-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
  margin-bottom: 4px;
}

.peer-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1.5px solid #f0ece3;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.peer-item:hover {
  border-color: rgba(214,182,116,0.45);
  background: rgba(214,182,116,0.04);
}

.peer-item--selected {
  border-color: #d6b674;
  background: rgba(214,182,116,0.08);
  box-shadow: 0 2px 8px rgba(214,182,116,0.15);
}

.peer-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f0ece3;
  flex-shrink: 0;
}

.peer-name {
  flex: 1;
  font-size: 0.88rem;
  font-weight: 600;
  color: #333;
}

.peer-check {
  color: #d6b674;
  font-size: 1rem;
}

.ack-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

.ack-btn {
  border: none;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;
  transition: all 0.18s;
}

.ack-btn--cancel {
  background: #fff0f0;
  color: #dc3545;
  border: 1px solid rgba(220,53,69,0.25);
}

.ack-btn--cancel:hover {
  background: #ffe0e0;
  color: #b02a37;
}

.ack-btn--close {
  background: #f4f4f4;
  color: #888;
}

.ack-btn--close:hover {
  background: #e8e8e8;
  color: #555;
}

.ack-btn--thanks {
  background: #e8e0d0;
  color: #b09050;
  opacity: 0.6;
  cursor: not-allowed;
}

.ack-btn--thanks-active {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: #fff;
  opacity: 1;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(214,182,116,0.35);
}

.ack-btn--thanks-active:hover {
  box-shadow: 0 4px 16px rgba(214,182,116,0.45);
  transform: translateY(-1px);
}
</style>
